import { lazy, Suspense, useMemo, useState } from "react";
import { sections } from "./content";
import { ScrollProvider, useScrollState } from "./scroll/ScrollProvider";
import { useReducedMotion } from "./lib/reducedMotion";
import { ecosystemNodes } from "./three/sceneData";

const Stage = lazy(() => import("./three/Stage"));

function Narrative() {
  const reducedMotion = useReducedMotion();
  const { sectionIndex } = useScrollState();
  const [activeNodeId, setActiveNodeId] = useState(ecosystemNodes[0].id);
  const activeId = sections[sectionIndex]?.id ?? sections[0].id;
  const sourceCount = useMemo(() => sections.flatMap((section) => section.stats ?? []).length, []);
  const activeNode = ecosystemNodes.find((node) => node.id === activeNodeId) ?? ecosystemNodes[0];

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main">
        Skip to narrative
      </a>

      <div className="stage-layer" aria-hidden={reducedMotion}>
        {!reducedMotion ? (
          <Suspense fallback={<div className="stage-fallback" />}>
            <Stage />
          </Suspense>
        ) : (
          <div className="stage-fallback is-reduced" />
        )}
      </div>

      <header className="masthead" aria-label="Prototype status">
        <span>BoM concept prototype</span>
        <span>Synthetic visual data</span>
        <span>{sourceCount} sourced figure groups</span>
      </header>

      <main id="main">
        {sections.map((section, index) => (
          <section
            className={`story-section ${activeId === section.id ? "is-active" : ""}`}
            data-section={section.id}
            key={section.id}
            aria-labelledby={`${section.id}-title`}
          >
            <div className="poster" role="img" aria-label={`${section.eyebrow} visual fallback`} />
            <div className="copy-panel">
              <p className="eyebrow">{section.eyebrow}</p>
              {index === 0 ? (
                <h1 id={`${section.id}-title`}>{section.headline}</h1>
              ) : (
                <h2 id={`${section.id}-title`}>{section.headline}</h2>
              )}
              {section.dek ? <p className="dek">{section.dek}</p> : null}
              <div className="body-copy">
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
              {section.stats ? (
                <dl className="stat-grid" aria-label={`${section.eyebrow} sourced figures`}>
                  {section.stats.map((stat) => (
                    <div key={stat.id}>
                      <dt>{stat.value}</dt>
                      <dd>{stat.source}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {section.id === "ecosystem" ? (
                <div className="network-controls" aria-label="Ecosystem graph nodes">
                  <div className="node-buttons">
                    {ecosystemNodes.map((node) => (
                      <button
                        type="button"
                        key={node.id}
                        className={node.id === activeNodeId ? "is-selected" : ""}
                        onClick={() => setActiveNodeId(node.id)}
                        aria-pressed={node.id === activeNodeId}
                      >
                        {node.label}
                      </button>
                    ))}
                  </div>
                  <p role="status">
                    <strong>{activeNode.label}</strong>
                    {activeNode.role}
                  </p>
                </div>
              ) : null}
            </div>
          </section>
        ))}

        <section className="engagement-panel" aria-labelledby="engage-title">
          <div>
            <p className="eyebrow">Concept engagement</p>
            <h2 id="engage-title">Explore a disclosure-grade reference layer.</h2>
            <p>
              This prototype does not submit data. It frames the intended conversation: reference layers,
              event certification, BARRA2 scenario support, and cost-recovered delivery.
            </p>
          </div>
          <form aria-label="Concept contact form" onSubmit={(event) => event.preventDefault()}>
            <label>
              Organisation
              <input type="text" name="organisation" autoComplete="organization" />
            </label>
            <label>
              Area of interest
              <select name="interest" defaultValue="reference-layer">
                <option value="reference-layer">National Hazard Reference Layer</option>
                <option value="barra2">BARRA2 disclosure reference</option>
                <option value="certification">Event parameter certification</option>
                <option value="delivery">Decision-grade delivery</option>
              </select>
            </label>
            <button type="submit">Register interest</button>
          </form>
        </section>
      </main>
    </div>
  );
}

export function App() {
  return (
    <ScrollProvider sectionCount={sections.length}>
      <Narrative />
    </ScrollProvider>
  );
}
